// src/components/recipes/RecipeViewModal.js
import React, { useEffect, useState } from "react";
import { Modal, Button, Row, Col, Image, Carousel } from "react-bootstrap";
import recipesAPI from "../../services/recipesAPI";
import Loader from "../loader/loader";

const RecipeViewModal = ({ recipeId, show, onHide }) => {
  const [loading, setLoading] = useState(false);
  const [recipe, setRecipe] = useState(null);
  const [activeTab, setActiveTab] = useState("products"); // products | ingredients | steps

  useEffect(() => {
    const fetchDetail = async () => {
      if (!recipeId || !show) return;
      setLoading(true);
      try {
        const resp = await recipesAPI.getRecipeDetail(recipeId);
        const payload = resp.data || resp;
        const recipeData = payload.recipe || payload;
        setRecipe(recipeData);
        setActiveTab("products");
      } catch (e) {
        console.error("Failed to load recipe detail", e);
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [recipeId, show]);

  const closeModal = () => {
    setRecipe(null);
    onHide && onHide();
  };

  if (!show) return null;

  const mediaList = recipe?.media || [];
  const ingredientGroups = recipe?.ingredientGroups || [];
  const stepSections = recipe?.stepSections || [];

  const getImageUrl = (fileUrl) =>
    fileUrl ? `${process.env.REACT_APP_IMAGE_URL}/uploads/${fileUrl}` : "";

  // primary first, then others
  const orderedMedia = [...mediaList].sort(
    (a, b) => (b.isPrimary || 0) - (a.isPrimary || 0)
  );

  // ---------- Video helpers ----------
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    try {
      let videoId = null;

      if (url.includes("youtu.be/")) {
        videoId = url.split("youtu.be/")[1].split(/[?&]/)[0];
      } else if (url.includes("youtube.com")) {
        const params = new URL(url).searchParams;
        videoId = params.get("v");
      }

      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } catch {
      return null;
    }
  };

  const embedUrl = getYouTubeEmbedUrl(recipe?.videoUrl);

  // ---------- Products ----------
  const rawProducts =
    recipe?.productItems ||
    recipe?.recipeProducts ||
    recipe?.mappedProducts ||
    [];

  const mappedProducts = rawProducts.map((p) => {
    const item = p.productItem || p;
    const unitSpec = item?.item_spec ?? item?.unit;
    const unitName = item?.item_unit ?? item?.unitName;
    const unitText = [unitSpec, unitName].filter(Boolean).join(" ");

    const baseLabel =
      p.label ||
      item?.itemName ||
      item?.productName ||
      item?.name ||
      item?.product?.productName ||
      "";

    const code =
      p.code ||
      item?.refCode ||
      item?.item_ref_code ||
      item?.itemCode ||
      item?.code ||
      "";

    const qty =
      p.quantityText ||
      p.defaultQty ||
      p.default_quantity ||
      p.defaultqty ||
      "";

    return {
      id: p.id || item?.id,
      label: unitText ? `${baseLabel} - ${unitText}` : baseLabel,
      code,
      quantityText: qty,
    };
  });

  const renderProductsTab = () => (
    <div className="mt-3">
      {mappedProducts.length === 0 && (
        <p className="text-muted mb-0">No products mapped for this recipe.</p>
      )}
      {mappedProducts.length > 0 && (
        <ul className="mb-0">
          {mappedProducts.map((p) => (
            <li key={p.id || p.label}>
              <strong>{p.label}</strong>
              {p.code ? ` (${p.code})` : ""}
              {p.quantityText ? ` – ${p.quantityText}` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const renderIngredientsTab = () => (
    <div className="mt-3">
      {ingredientGroups.length === 0 && (
        <p className="text-muted mb-0">No ingredients available.</p>
      )}
      {ingredientGroups.map((g) => (
        <div key={g.id} className="mb-3">
          {g.title && <div className="fw-semibold mb-1">{g.title}</div>}
          <ul className="mb-0">
            {(g.ingredients || []).map((ing) => (
              <li key={ing.id}>
                {ing.ingredientName}
                {ing.quantityText ? ` – ${ing.quantityText}` : ""}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  const renderStepsTab = () => (
    <div className="mt-3">
      {stepSections.length === 0 && (
        <p className="text-muted mb-0">No instructions available.</p>
      )}
      {stepSections.map((sec) => {
        const steps = sec.steps || sec.lines || [];
        return (
          <div key={sec.id} className="mb-3">
            {sec.title && <div className="fw-semibold mb-1">{sec.title}</div>}
            <ul className="mb-0">
              {steps.map((st) => (
                <li key={st.id}>{st.description}</li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );

  const TabButton = ({ id, children }) => {
    const isActive = activeTab === id;
    return (
      <button
        type="button"
        onClick={() => setActiveTab(id)}
        className={`border-0 px-3 py-2 rounded-pill text-sm ${
          isActive ? "bg-dark text-white" : "bg-light text-muted"
        }`}
        style={{
          fontSize: "0.85rem",
          fontWeight: isActive ? 600 : 500,
          minWidth: 110,
        }}
      >
        {children}
      </button>
    );
  };

  return (
    <Modal show={show} onHide={closeModal} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{recipe?.title || "Recipe Details"}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: "80vh", overflowY: "auto" }}>
        {loading && <Loader />}
        {!loading && recipe && (
          <>
            {/* VIDEO SECTION (top) */}
            {embedUrl && (
              <Row className="mb-3">
                <Col>
                  <div
                    style={{
                      position: "relative",
                      paddingTop: "56.25%", // 16:9
                      borderRadius: 16,
                      overflow: "hidden",
                    }}
                  >
                    <iframe
                      src={embedUrl}
                      title={recipe.title}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        border: 0,
                      }}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                </Col>
              </Row>
            )}

            {/* IMAGE SLIDER */}
            {orderedMedia.length > 0 && (
              <Row className="mb-3">
                <Col>
                  <Carousel
                    interval={null}
                    indicators={orderedMedia.length > 1}
                    slide={orderedMedia.length > 1}
                  >
                    {orderedMedia.map((m) => (
                      <Carousel.Item key={m.id || m.fileUrl}>
                        <Image
                          src={getImageUrl(m.fileUrl)}
                          alt={m.altText || recipe.title}
                          style={{
                            width: "100%",
                            maxHeight: 230,
                            objectFit: "cover",
                            borderRadius: 16,
                          }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                </Col>
              </Row>
            )}

            {/* TITLE + DESCRIPTION */}
            <Row className="mb-3">
              <Col>
                <h5 className="mb-1">{recipe.title}</h5>
                {recipe.description && (
                  <p
                    className="mb-0"
                    style={{ whiteSpace: "pre-line", fontSize: "0.9rem" }}
                  >
                    {recipe.description}
                  </p>
                )}
              </Col>
            </Row>

            {/* TABS */}
            <Row className="mb-2">
              <Col className="d-flex justify-content-center">
                <div
                  className="d-inline-flex align-items-center"
                  style={{
                    background: "red",
                    borderRadius: 999,
                    padding: 10,
                    columnGap: 40, 
                  }}
                >
                  <TabButton id="products">Products</TabButton>
                  <TabButton id="ingredients">Ingredients</TabButton>
                  <TabButton id="steps">How to Make</TabButton>
                </div>
              </Col>
            </Row>

            {/* TAB CONTENT */}
            {activeTab === "products" && renderProductsTab()}
            {activeTab === "ingredients" && renderIngredientsTab()}
            {activeTab === "steps" && renderStepsTab()}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={closeModal}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default RecipeViewModal;
